import type { FieldMetadata, FormMetadata } from '@conform-to/react';
import {
  FormProvider,
  getFormProps,
  getInputProps,
  getTextareaProps,
  useForm,
  useFormMetadata,
  useInputControl,
} from '@conform-to/react';
import { parseWithZod } from '@conform-to/zod';
import type { ComponentPropsWithRef, HTMLAttributes } from 'react';
import { type ZodSchema } from 'zod';
import { useSpinDelay } from 'spin-delay';
import type { ComboboxOption } from 'ui';
import {
  Combobox,
  Button,
  Checkbox,
  CheckboxField,
  Description,
  ErrorMessage,
  Field,
  Input,
  Listbox,
  ListboxLabel,
  ListboxOption,
  Select,
  Textarea,
} from 'ui';
import { cn } from 'ui/src/lib/utils';
import { type FetcherWithComponents, Form } from '@remix-run/react';
import { useTranslationScope } from '~/localization/use-translation-scope';
import { createContext, useContext } from 'react';
import PhoneInput from 'ui/src/PhoneInput';

type FormOptions<Schema extends Record<string, any>, FormValue = Schema> = Parameters<
  typeof useForm<Schema, FormValue, string[]>
>[0];

/**
 * A hook to create a Conform form with Zod schema. This hook is a wrapper around
 * `useForm` and `parseWithZod`. It includes sensible defaults that are suitable
 * for most forms.
 *
 * Usage:
 * ```tsx
 * const schema = z.object({
 *   name: z.string().nonempty(),
 * });
 *
 * function MyForm() {
 *   const fetcher = useFetcher<typeof action>();
 *   const [form, fields] = useAppForm({
 *     schema,
 *     lastResult: fetcher.data,
 *     defaultValue: {
 *       name: '',
 *       email: '',
 *     }
 *   });
 *
 *   return (
 *     <Conform form={form} method="POST">
 *       ...fields
 *     </Conform>
 *   )
 * }
 * ```
 */
export function useAppForm<
  ZSchema extends ZodSchema,
  Schema extends ZSchema['_input'],
  FormValue = ZSchema['_output'],
>({ schema, ...options }: FormOptions<Schema, FormValue> & { schema: ZSchema }) {
  return useForm<Schema, FormValue>({
    onValidate({ formData }) {
      return parseWithZod(formData, { schema });
    },
    shouldValidate: 'onSubmit',
    shouldRevalidate: 'onBlur',
    ...options,
  });
}

type ConformProps<TSchema extends Record<string, unknown>, TFormError = string[]> = Omit<
  ComponentPropsWithRef<typeof Form>,
  'id'
> & {
  form: FormMetadata<TSchema, TFormError>;
  fetcher?: FetcherWithComponents<any>;
};

/**
 * A wrapper around the `Form` component that provides the form context so that
 * descendants can access the form via useFormMetadata.
 */
export function Conform<TSchema extends Record<string, unknown>, TFormError = string[]>({
  form,
  fetcher,
  ...props
}: ConformProps<TSchema, TFormError>) {
  const FormComponent = fetcher ? fetcher.Form : Form;
  return (
    <FormProvider context={form.context}>
      <FormComponent {...getFormProps(form, { ariaAttributes: true })} {...props} />
    </FormProvider>
  );
}

const FormFieldContext = createContext<{
  meta: FieldMetadata;
} | null>(null);

/**
 * A hook to access the form field context. This hook must be used within a
 * FormField and is meant for internal use only.
 */
export function useFormFieldContext() {
  const context = useContext(FormFieldContext);
  if (!context) {
    throw new Error('Field Component must be used within a FormField');
  }
  return context;
}

/**
 * This is a hidden field on the form that can be used as a discriminated union
 * for pages that have multiple forms posting to a single action.
 *
 * When using an intent, you should define the intent in the zod schema as a
 * discriminated union type. Then include the FormIntent component in each form
 * to specify the intent for each form.
 *
 * ```tsx
 * // create an intent field in your schema
 * const schema = z.discriminatedUnion('intent', [
 *  z.object({
 *    intent: z.literal('create'),
 *    name: z.string(),
 *  }),
 *  z.object({
 *    intent: z.literal('destroy')
 *    id: z.string(),
 *  })
 * ])
 *
 * function MyForm() {
 *.  const createFetcher = useFetcher<typeof action>();
 *   const [createForm, createFields] = useAppForm({
 *     schema,
 *     lastResult: createFetcher.data,
 *   });
 *
 *   const destroyFetcher = useFetcher<typeof action>();
 *   const [destroyForm, destroyFields] = useAppForm({
 *    schema,
 *    lastResult: destroyFetcher.data,
 *    });
 *
 *    return (
 *    <>
 *      <Conform form={createForm} method="POST" fetcher={createFetcher}>
 *         <FormIntent meta={createFields.intent} intent="create"/>
 *         <FormField meta={createFields.name}>
 *           <Label>Name</Label>
 *           <FieldInput type="text" />
 *         </FormField>
 *      </Conform>
 *      <Conform form={destroyForm} method="POST" fetcher={destroyFetcher}>
 *        <FormIntent meta={destroyFields.intent} intent="destroy" />
 *        <FormField meta={destroyFields.id}>
 *          <FieldInput type="hidden" />
 *          <Label>ID</Label>
 *        </FormField>
 *      </Conform>)
 *    </>
 * }
 * ```
 */
export function FormIntent<TSchema, TFormSchema extends { intent: string } & Record<string, any>>({
  meta,
  intent,
}: {
  meta: FieldMetadata<TSchema, TFormSchema>;
  intent: TFormSchema['intent'];
}) {
  return <input {...getInputProps(meta, { type: 'hidden' })} key={meta.key} defaultValue={intent} />;
}

/**
 * A component to provide the form field context to it's descendents. The
 * FormField component is a wrapper around the HeadlessUI Field component. It
 * provides the field metadata to it's descendents via the FormFieldContext and
 * ensures that all field elements are accessible to screen readers.
 *
 * Usage:
 * ```tsx
 * <FormField meta={fields.name}>
 *   <Label>Name</Label>
 *   <FieldInput type="text" />
 *   <FieldDescription>Enter your full name</FieldDescription>
 *   <FieldErrors />
 * </FormField>
 * ```
 *
 * Because the FormField component provides the field metadata to its
 * descendents, the FieldInput, FieldDescription, and FieldErrors components
 * can be used without any additional props.
 */
export function FormField({ meta, ...props }: { meta: FieldMetadata } & ComponentPropsWithRef<typeof Field>) {
  return (
    <FormFieldContext
      value={{
        meta,
      }}
    >
      <Field {...props} />
    </FormFieldContext>
  );
}

type FieldDescriptionProps = Omit<ComponentPropsWithRef<'p'>, 'id'> & {
  hideOnErrors?: boolean;
};

/**
 * A component to display a description for a form field. The description is
 * hidden when there are errors on the field by default.
 *
 * Note: Use normal Description component with FieldCheckbox to not hide the
 * description on errors.
 *
 * @param hideOnErrors - Hide the description when there are errors on the field.
 */
export function FieldDescription({ className, hideOnErrors = true, ...props }: FieldDescriptionProps) {
  const { meta } = useFormFieldContext();

  if (meta.errors?.length && hideOnErrors) return null;

  return <Description {...props} />;
}

type FieldErrorsProps = Omit<React.HTMLAttributes<HTMLElement>, 'id'>;

/**
 * A component to display the errors for a form field. The errors are displayed
 * as a list when there are multiple errors.
 */
export function FieldErrors({ className, ...props }: FieldErrorsProps) {
  const { meta } = useFormFieldContext();
  const { t } = useTranslationScope('form');

  if (!meta.errors?.length) return null;

  return meta.errors.length === 1 ? (
    <ErrorMessage className={cn('!text-red-500 dark:!text-red-400', className)} {...props}>
      {t(meta.errors[0])}
    </ErrorMessage>
  ) : (
    <ErrorMessage>
      <ul className={cn('flex list-inside list-disc flex-col gap-1', className)} {...props}>
        {meta.errors.map((error, index) => (
          <li key={index} className="!text-red-500 dark:!text-red-400">
            {t(error)}
          </li>
        ))}
      </ul>
    </ErrorMessage>
  );
}

type FormErrorsProps = HTMLAttributes<HTMLElement>;

/**
 * A component to display the form errors. The errors are displayed as a list
 * when there are multiple errors. The FormErrors component must be used within
 * a Conform component.
 *
 * Usage:
 * ```tsx
 * export function action({request}) {
 *   ...
 *   // return a form error
 *   return json(submission.reply({formErrors: ['This is a form error']}), {status: 400});
 * }
 *
 * ...
 * <Conform form={form}>
 *   ...
 *   <FormErrors />
 * </Conform>
 */
export function FormErrors({ className, ...props }: FormErrorsProps) {
  const { errors } = useFormMetadata();
  const { t } = useTranslationScope('form');

  if (!errors) return null;

  if (errors.length === 1) {
    return (
      <p className={cn('!text-red-500 dark:!text-red-400', className)} {...props}>
        {t(errors[0])}
      </p>
    );
  }

  return (
    <ul className={cn('flex list-inside list-disc flex-col gap-1', className)} {...props}>
      {errors.map((error, index) => (
        <li key={index} className="!text-red-500 dark:!text-red-400">
          {t(error)}
        </li>
      ))}
    </ul>
  );
}

type TypeValues =
  | 'checkbox'
  | 'color'
  | 'date'
  | 'datetime-local'
  | 'email'
  | 'file'
  | 'hidden'
  | 'month'
  | 'number'
  | 'password'
  | 'radio'
  | 'range'
  | 'search'
  | 'tel'
  | 'text'
  | 'time'
  | 'url'
  | 'week';

// Disabled all constraint fields becuase they come from conform automatically
// with getInputProps when defined on the zod schema. Magic.
export type FieldInputProps = Omit<
  ComponentPropsWithRef<typeof Input>,
  'id' | 'name' | 'min' | 'max' | 'minLength' | 'maxLength' | 'step' | 'pattern' | 'multiple'
> &
  Required<{
    type: Exclude<TypeValues, 'checkbox' | 'radio'>;
  }>;

/**
 * A text input field that is connected to a form field. The FieldInput component
 * is a wrapper around the HeadlessUI Input component. It provides all of the
 * constraint props (min, max, minLength, maxLength, step, pattern, multiple) to
 * the underlying input component automatically, based on the zod schema.
 *
 * Note: this must be used within a FormField component.
 *
 * @param type - The type of the input field.
 */
export function FieldInput({ type, ...props }: FieldInputProps) {
  const { meta } = useFormFieldContext();

  return (
    <Input
      {...getInputProps(meta, { type, ariaAttributes: false })}
      invalid={(meta.errors?.length ?? 0) > 0}
      key={meta.key}
      {...props}
    />
  );
}

type FieldSelectProps = Omit<
  ComponentPropsWithRef<'select'>,
  'id' | 'name' | 'onChange' | 'onBlur' | 'onFocus' | 'defaultValue' | 'value'
> & {
  options: Array<{ label: string; value: string }>;
  placeholder: string;
};

/**
 * A select field that is connected to a form field. The FieldSelect component
 * is a wrapper around the native select element.
 *
 * Note: this must be used within a FormField component.
 * @param options - An array of objects with a label and value.
 * @param placeholder
 */
export function FieldSelect({ options, placeholder }: FieldSelectProps) {
  const { meta } = useFormFieldContext();
  const { value, blur, focus, change } = useInputControl(meta as FieldMetadata<string>);

  return (
    <Select
      name={meta.name}
      id={meta.id}
      value={value}
      defaultValue={meta.initialValue as string}
      onChange={(e) => change(e.target.value)}
      onBlur={blur}
      onFocus={focus}
    >
      <option value="" disabled>
        {placeholder}
      </option>
      {options.map(({ label, value }) => (
        <option value={value} key={value}>
          {label}
        </option>
      ))}
    </Select>
  );
}

type FieldListboxProps = Omit<
  ComponentPropsWithRef<typeof Listbox>,
  'value' | 'onChange' | 'id' | 'name' | 'defaultValue'
> & {
  options: Array<{ label: string; value: string }> | ReadonlyArray<{ label: string; value: string }>;
};

/**
 * A listbox field that is connected to a form field. The FieldListbox component
 * is a wrapper around the HeadlessUI Listbox component.
 *
 * Note: this must be used within a FormField component.
 * @param options - An array of objects with a label and value.
 */
export function FieldListbox({ options, ...props }: FieldListboxProps) {
  const { meta } = useFormFieldContext();

  return (
    <Listbox name={meta.name} defaultValue={meta.initialValue} invalid={(meta.errors?.length ?? 0) > 0} {...props}>
      {options.map(({ label, value }) => (
        <ListboxOption value={value} key={value}>
          <ListboxLabel>{label}</ListboxLabel>
        </ListboxOption>
      ))}
    </Listbox>
  );
}

type FieldComboboxProps = Omit<ComponentPropsWithRef<typeof Combobox>, 'id' | 'name'>;

export function FieldCombobox({
  options,
  placeholder,
  ...props
}: FieldComboboxProps & { options: ComboboxOption[]; placeholder?: string }) {
  const { meta } = useFormFieldContext();
  const { blur } = useInputControl(meta as FieldMetadata<string>);

  const form = useFormMetadata();

  return (
    <Combobox
      name={meta.name}
      id={meta.id}
      onBlur={() => {
        blur();
        form.validate({ name: meta.name });
      }}
      defaultValue={meta.initialValue as string | undefined}
      invalid={(meta.errors?.length ?? 0) > 0}
      options={options}
      placeholder={placeholder}
      {...props}
    />
  );
}

type FieldTextareaProps = Omit<
  ComponentPropsWithRef<typeof Textarea>,
  'id' | 'value' | 'onChange' | 'onBlur' | 'onFocus' | 'defaultValue'
>;

/**
 * A textarea field that is connected to a form field. The FieldTextarea component
 * is a wrapper around the HeadlessUI Textarea component.
 *
 * Note: this must be used within a FormField component.
 */
export function FieldTextarea(props: FieldTextareaProps) {
  const { meta } = useFormFieldContext();

  return <Textarea {...props} {...getTextareaProps(meta, { ariaAttributes: false })} key={meta.key} />;
}

type FormCheckboxFieldProps = ComponentPropsWithRef<typeof CheckboxField> & {
  meta: FieldMetadata;
};

/**
 * A checkbox form field that provides the field metadata as context to it's
 * descendants.
 *
 * Note: Use a normal Description component with this field to not hide the
 * description on errors.
 *
 * Usage:
 * ```tsx
 * // With a label
 * <FormCheckboxField meta={fields.enabled}>
 *   <FieldCheckbox />
 *   <Label>My Checkbox</Label>
 *   <FieldErrors />
 * </FormCheckboxField>
 *
 * // With a label and description
 * <FormCheckboxField meta={fields.enabled}>
 *   <FieldCheckbox />
 *   <Label>My Checkbox</Label>
 *   <Description>Enable this feature</Description>
 *   <FieldErrors />
 * </FormCheckboxField>
 * ```
 */
export function FormCheckboxField({ meta, ...props }: FormCheckboxFieldProps) {
  return (
    <FormFieldContext
      value={{
        meta,
      }}
    >
      <CheckboxField {...props} />
    </FormFieldContext>
  );
}

type FieldCheckboxProps = Omit<ComponentPropsWithRef<typeof Checkbox>, 'name'> & {
  value?: string;
  onChange?: (checked: boolean) => void;
};

/**
 * Renders a checkbox within a FormCheckboxField. The FieldCheckbox component
 * is a wrapper around the HeadlessUI Checkbox component.
 *
 * Note: this must be used within a FormCheckboxField component.
 */
export function FieldCheckbox({ onChange: _onChange, ...props }: FieldCheckboxProps) {
  const { meta } = useFormFieldContext();
  const { value, blur, change, focus } = useInputControl(meta as FieldMetadata<string>);
  const checkedValue = props.value ?? 'on';

  function handleChange(checked: boolean) {
    change(checked ? checkedValue : '');
    _onChange?.(checked);
  }

  return (
    <>
      {/* HeadlessUI doesn't render the hidden input immediately, which causes
          Conform to render a dummy field automatically. This is problematic
          because after Headless UI renders the hidden input, we now have two
          form elements with the same name, resulting in array values for the
          single checkbox. To remediate, we will render our own hidden input
          to contain the checkbox value, and remove the name property from the
          HeadlessUI checkbox so it does not try to create its own. */}
      <input {...getInputProps(meta, { type: 'hidden', value: false })} value={value} />
      <Checkbox
        defaultChecked={meta.initialValue === checkedValue}
        value={checkedValue}
        checked={value === checkedValue}
        onChange={handleChange}
        onBlur={blur}
        onFocus={focus}
        {...props}
      />
    </>
  );
}

type FieldPhoneInputProps = Omit<ComponentPropsWithRef<typeof PhoneInput>, 'name' | 'invalid'>;

export function FieldPhoneInput({ placeholder, countryCodes, ...props }: FieldPhoneInputProps) {
  const { meta } = useFormFieldContext();

  return (
    <PhoneInput
      {...getInputProps(meta, { type: 'tel', ariaAttributes: false })}
      invalid={(meta.errors?.length ?? 0) > 0}
      key={meta.key}
      name={meta.name}
      countryCodes={countryCodes}
      placeholder={placeholder}
      {...props}
    />
  );
}

type StatusButtonProps = ComponentPropsWithRef<typeof Button> & {
  type?: 'submit';
  spinDelay?: Parameters<typeof useSpinDelay>[1];
};

/**
 * A button that shows a loading spinner when the loading prop is true. It uses
 * the useSpinDelay hook to prevent the spinner from flashing when the loading
 * state is short lived.
 */
export function StatusButton({ children, loading, spinDelay, ...props }: StatusButtonProps) {
  const delayedPending = useSpinDelay(!!loading, {
    delay: 300,
    minDuration: 300,
    ...spinDelay,
  });

  return (
    <Button type="submit" {...props} loading={delayedPending}>
      {children}
    </Button>
  );
}
